/**
 * Estado de una organización (tenant) tal como lo ve la UI: la consola de
 * plataforma (OT-0073 §4.3) y el shell de los usuarios del cliente (§4.4).
 *
 * El acceso lo calcula el backend (`access`, `permissions.tenant_access`);
 * aquí sólo se traduce a la etiqueta y, para mostrar cuándo empieza la sólo
 * lectura, se usa `trial_ends_at`.
 */

import type { TenantAccess, TenantStatus } from '../api/platform'

export type OrganizationState = 'beta' | 'active' | 'read_only' | 'suspended'

export const ORGANIZATION_STATES: readonly OrganizationState[] = [
  'beta',
  'active',
  'read_only',
  'suspended',
]

/** Beta vencida → sólo lectura; suspendido gana sobre todo lo demás. */
export function organizationState({
  status,
  access,
}: {
  status: TenantStatus
  access: TenantAccess
}): OrganizationState {
  if (access === 'suspended') return 'suspended'
  if (access === 'read_only') return 'read_only'
  return status === 'trial' ? 'beta' : 'active'
}

/** Tono del badge por estado. Sólo tokens de `Docs/18-BRAND.md`. */
export function organizationStateTone(state: OrganizationState): string {
  switch (state) {
    case 'beta':
      return 'bg-brand-soft text-brand'
    case 'active':
      return 'bg-official-soft text-official'
    case 'read_only':
      return 'bg-review-soft text-review'
    case 'suspended':
      return 'bg-critical-soft text-critical'
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Un día elegido en un selector de fecha (`YYYY-MM-DD`) como el instante en que
 * empieza ese día en la zona del navegador. Es el criterio de toda fecha de fin
 * que fija la UI (p. ej. extender la beta): la beta termina al empezar ese día,
 * así que ese día es el primero en sólo lectura, y `formatDay` —también en hora
 * local— lo muestra tal cual se eligió.
 */
export function startOfLocalDay(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString()
}

/** Fecha del día en el idioma y la zona del usuario, sin hora. */
export function formatDay(iso: string | null, locale: string): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso))
}

/**
 * Días de calendario, en la zona del usuario, entre hoy y el día en que la
 * organización pasa a sólo lectura (`trial_ends_at`): 0 si es hoy, 1 si es
 * mañana, negativo si ya pasó; null sin fecha. Cuenta días y no horas para que
 * el último día de beta se lea "desde mañana" y no "vence hoy".
 */
export function daysUntilReadOnly(
  trialEndsAt: string | null,
  now: Date = new Date(),
): number | null {
  if (!trialEndsAt) return null
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  // `round`: un día con cambio de horario dura 23 o 25 horas.
  return Math.round((startOfDay(new Date(trialEndsAt)) - startOfDay(now)) / DAY_MS)
}

/** Con cuántos días de anticipación se avisa a los admins que se acerca la
 *  sólo lectura. */
export const TRIAL_ENDING_NOTICE_DAYS = 14
